import { useEffect, useState } from 'react';
import { Box, Card, Heading, Icon, Text, TextButton } from '@zillow/constellation-10';
import { IconTrophyFilled } from '@zillow/constellation-icons';
import type { YourRentalRewardsBonusOfferDto, YourRentalRewardsCardDto } from '@zg-rentals/ts-your-rental-api-client';
import { RewardsApiTransactionType } from '@zg-rentals/ts-your-rental-api-client';
import { useYourRentalWebApiHandler_enrollRewardsUser } from '@zg-rentals/ts-your-rental-api-client-react-query';
import { RewardsEnrollmentModal } from '@zg-rentals/rewards-enrollment-modal';
import { client } from '../../rentalClient';
import CoinIcon from './CoinIcon';

const MAX_DOLLAR_VALUE = 4000;
const CONTACT_CARE_URL = 'mailto:rentpayments@zillow.com';

type RewardsUpsellCardProps = {
  rewardsCard: YourRentalRewardsCardDto;
  bonusOffers?: Array<YourRentalRewardsBonusOfferDto>;
  autoOpenEnroll?: boolean;
  isRentPaymentRenter?: boolean;
};

function getClaimablePoints(bonusOffers?: Array<YourRentalRewardsBonusOfferDto>): number {
  const enrollmentBonus = bonusOffers?.find(
    (offer) => offer.transactionType === RewardsApiTransactionType.ENROLLMENT_BONUS,
  );
  return enrollmentBonus ? parseInt(enrollmentBonus.points, 10) || 0 : 0;
}

export default function RewardsUpsellCard({
  rewardsCard,
  bonusOffers,
  autoOpenEnroll,
  isRentPaymentRenter,
}: RewardsUpsellCardProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (autoOpenEnroll) {
      setIsModalOpen(true);
    }
  }, [autoOpenEnroll]);
  const {
    mutateAsync: enrollUser,
    isPending: isEnrolling,
    isError: hasEnrollmentError,
  } = useYourRentalWebApiHandler_enrollRewardsUser({
    httpClient: client,
    meta: {
      context: {
        api: 'enrollRewardsUser',
        details: 'enrolling user in rewards from your-rental page',
      },
    },
  });

  const { cardTitle, cardDescription, ctas } = rewardsCard;
  const ctaText = ctas?.[0]?.text ?? 'Claim your points';
  const claimablePoints = getClaimablePoints(bonusOffers);

  const handleEnroll = async () => {
    try {
      await enrollUser();
      window.location.href = '/rental/rewards?enrolled=true';
    } catch {
      // Modal displays error state via hasEnrollmentError
    }
  };

  return (
    <>
      <Card
        elevated={false}
        css={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          padding: 'loose',
          gap: 'default',
        }}
        data-testid="RewardsUpsellCard"
      >
        <Icon size="lg" css={{ color: 'text.accent.purple.hero' }}>
          <IconTrophyFilled />
        </Icon>
        <Box css={{ display: 'flex', flexDirection: 'column' }}>
          <Heading level={3} css={{ textStyle: 'body-bold' }}>
            {cardTitle}
          </Heading>
          {cardDescription && <Text>{cardDescription}</Text>}
        </Box>
        <Box
          css={{
            display: 'flex',
            alignItems: 'center',
            gap: 'default',
          }}
        >
          <TextButton
            textStyle="body-bold"
            tone="brand"
            onClick={() => setIsModalOpen(true)}
            data-testid="RewardsUpsellCard-Cta"
          >
            {ctaText}
          </TextButton>
          {claimablePoints > 0 && (
            <Box
              css={{
                display: 'flex',
                alignItems: 'center',
                gap: 'tighter',
                backgroundColor: 'bg.screen.softest',
                borderRadius: 'capsule',
                paddingBlock: 'tighter',
                paddingInline: 'tight',
              }}
              data-testid="RewardsUpsellCard-PointsTag"
            >
              <CoinIcon size={20} />
              <Text textStyle="body-sm">
                You have <Text textStyle="body-sm-bold">{claimablePoints.toLocaleString()}</Text> points
              </Text>
            </Box>
          )}
        </Box>
      </Card>
      <RewardsEnrollmentModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        onEnroll={handleEnroll}
        isRentPaymentRenter={isRentPaymentRenter}
        claimablePoints={claimablePoints}
        maxDollarValue={MAX_DOLLAR_VALUE}
        isLoading={isEnrolling}
        hasEnrollmentError={hasEnrollmentError}
        contactCareUrl={CONTACT_CARE_URL}
      />
    </>
  );
}
